import { api } from './api';

export type ApprovalKind = 'deal_discount' | 'outbound_template' | 'board_report' | 'contract_terms';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface ApprovalHistoryEntry {
  step: number;
  user_id: string;
  action: 'approved' | 'rejected';
  note: string | null;
  at: string;
}

export interface Approval {
  id: string;
  workspaceId: string;
  targetKind: ApprovalKind;
  targetId: string;
  requiredSigners: string[];
  currentStep: number;
  status: ApprovalStatus;
  history: ApprovalHistoryEntry[];
  createdAt: string;
}

export const approvalsApi = {
  async list(workspaceId: string): Promise<Approval[]> {
    const r = await api.get<Approval[]>(`/api/workspaces/${workspaceId}/approvals`);
    return r.data;
  },
  async act(approvalId: string, action: 'approve' | 'reject', note?: string): Promise<Approval> {
    const r = await api.post<Approval>(`/api/approvals/${approvalId}/act`, { action, note });
    return r.data;
  },
  async create(input: {
    workspaceId: string;
    targetKind: ApprovalKind;
    targetId: string;
    requiredSigners: string[];
  }): Promise<Approval> {
    const r = await api.post<Approval>(`/api/workspaces/${input.workspaceId}/approvals`, {
      targetKind: input.targetKind,
      targetId: input.targetId,
      requiredSigners: input.requiredSigners,
    });
    return r.data;
  },
  /**
   * Fetches the hanko-stamped PDF for an approval and triggers a browser
   * download. The server returns `application/pdf` with `Content-Disposition:
   * attachment; filename="approval-{id}.pdf"`; we honour that filename if
   * present, else fall back to a sensible default.
   */
  async downloadHankoPdf(approvalId: string): Promise<void> {
    const r = await api.get<Blob>(`/api/approvals/${approvalId}/hanko-pdf`, {
      responseType: 'blob',
    });
    const disposition = (r.headers as Record<string, string | undefined>)?.[
      'content-disposition'
    ];
    let filename = `approval-${approvalId}.pdf`;
    if (disposition) {
      const m = /filename="([^"]+)"/.exec(disposition);
      if (m && m[1]) filename = m[1];
    }
    const blob = r.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },
};
